import { FormControl, RadioGroup } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LoadingButton } from "../../../../../../common/components/LoadingButton";
import { NetworkRequestGate } from "../../../../../../common/components/NetworkRequestGate";
import { useActionRequest } from "../../../../../../common/hooks/api";
import { Api } from "../../../../../../common/service/Api";
import { DataSubjectRequestDetailDto } from "../../../../../../common/service/server/dto/DataSubjectRequestDto";
import { OrganizationDataRecipientDto } from "../../../../../../common/service/server/dto/OrganizationDataRecipientDto";
import {
  OrganizationAuthorizationsDto,
  OrganizationDto,
} from "../../../../../../common/service/server/dto/OrganizationDto";
import { VendorOutcomeOption } from "../../../../../../common/service/server/Types";
import { usePlatformApps } from "../../../../../hooks/useOrganizationVendors";
import { installedAppsRespectingDeletionForVendor } from "../../../../../util/resources/vendorUtils";
import {
  isFacebookAds,
  isGoogleAnalytics,
  isShopify,
  isZendesk,
} from "../../../../../util/vendors";
import { Callout, CalloutVariant } from "../../../../Callout";
import {
  ConnectedSystemAutomationCallout,
  isAwaitingConnectedSystemAutomation,
} from "./ConnectedSystemAutomationCallout";
import { NestedVendorList } from "./NestedVendorList";
import { DataRecipientLogo } from "../../../../DataRecipientLogo";
import { VendorEmailDisplayDialog } from "../../../../dialog/VendorEmailDisplayDialog";
import { RadioControlLabel } from "../../../../input/RadioControlLabel";
import { VendorOutcomeCollection, VendorOutcomeHandler } from "./../delete/DeleteInfo";
import { InstructionsButton, InstructionsContent } from "./CollapsibleInstructions";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";
import clsx from "clsx";
import "./shared-styles.css";
import { Routes } from "../../../../../root/routes";

const getAutomationCallout = (automationVendorResult: string) => {
  switch (automationVendorResult) {
    case "AUTOMATION_REQUESTED":
      return (
        <Callout
          variant={CalloutVariant.Green}
          title="Request to delete data automatically sent"
        ></Callout>
      );
    case "AUTOMATION_CONFIRMED":
      return <Callout variant={CalloutVariant.Green} title="Data automatically deleted"></Callout>;
    case "AUTOMATION_NO_DATA":
      return (
        <Callout
          variant={CalloutVariant.Secondary}
          title="Vendor indicated no data to delete for the consumer"
        ></Callout>
      );
    default:
      return null;
  }
};

export const VendorDeleteRow: React.FC<{
  vendor: OrganizationDataRecipientDto;
  vendorOutcomes: VendorOutcomeCollection;
  authorizations: OrganizationAuthorizationsDto;
  org: OrganizationDto;
  onSelectionChange: VendorOutcomeHandler;
  request: DataSubjectRequestDetailDto;
  updatePending?: boolean;
  customAudienceManagementVendor?: OrganizationDataRecipientDto;
  managedCustomAudienceVendors?: OrganizationDataRecipientDto[];
  instructionsState?: {
    toggleInstructions: (id: string) => void;
    isExpanded: (id: string) => boolean;
  };
}> = ({
  vendor,
  onSelectionChange,
  vendorOutcomes,
  authorizations,
  org,
  request,
  customAudienceManagementVendor,
  managedCustomAudienceVendors,
  updatePending = false,
  instructionsState,
}) => {
  const instruction = vendor.instructions.find((vi) => vi.requestType == "DELETE");

  const isPartialDeleteVendor = Boolean(instruction?.hasRetentionExceptions);
  const deleteLabel = isPartialDeleteVendor ? "Data partially deleted" : "Data deleted";
  const deleteValue = isPartialDeleteVendor ? "DATA_RETAINED" : "DATA_DELETED";
  const disabled = isGoogleAnalytics(vendor);
  const value = vendorOutcomes[vendor.vendorId] || ""; // Need to always have some value here to prevent MUI from treating `value` as uncontrolled
  const resolvedAutomatically = [
    "AUTOMATION_REQUESTED",
    "AUTOMATION_CONFIRMED",
    "AUTOMATION_NO_DATA",
  ].includes(value);

  const { refreshRequest } = useDataRequestProcessingContext();
  const [isAttemptingAutomation, setIsAttemptingAutomation] = useState(false);
  const submitRequestForAutomation = useCallback(async (orgId, requestId) => {
    return Api.dataRequest.manuallyAutomateRequest(orgId, requestId);
  }, []);

  const outcomeDto = request.vendorOutcomes.find((vo) => vo.vendor.id === vendor.vendorId);
  const isZendeskFailure =
    isZendesk(vendor) &&
    outcomeDto?.automationVendorTaskFailedMessage?.toLowerCase().includes("not closed");

  const reconnectNudge = (
    <>
      Try disconnecting and reconnecting to this vendor on the{" "}
      <Link className="text-weight-regular" to="/privacy-center/integrations">
        Integrations page
      </Link>
      , then press the Automate button to retry.
    </>
  );
  const metaAdsTOSReminder = (
    <>
      For Meta Ads, be sure you've logged in to the Meta Business Account at least once and accepted
      the Custom Audience Terms of Service.
    </>
  );

  // The Connected System is resolved by automation, so it gets an explanation instead of manual outcome controls.
  const awaitingConnectedSystem = isAwaitingConnectedSystemAutomation(vendor, value, request);

  const rowContent = resolvedAutomatically ? (
    getAutomationCallout(value)
  ) : awaitingConnectedSystem ? (
    <ConnectedSystemAutomationCallout />
  ) : (
    <>
      {value === "AUTOMATION_FAILED" && (
        <Callout
          variant={CalloutVariant.Yellow}
          title="Automatic deletion not successful"
          className="mb-sm"
          subtitle={
            isZendeskFailure ? (
              <div>
                <div>
                  This consumer still has active tickets in ZenDesk and could not be deleted
                  automatically. Please resolve these tickets (you may need to wait for ZenDesk to
                  archive them), then try again. See{" "}
                  <a
                    className="text-weight-regular"
                    href="https://developers.truevault.com/docs/automation/integration-zendesk"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    our ZenDesk automation guide
                  </a>{" "}
                  for more detail.
                </div>
                <div className="mt-sm">
                  <LoadingButton
                    variant="contained"
                    className="manually-automate-request-button"
                    loading={isAttemptingAutomation}
                    onClick={async () => {
                      setIsAttemptingAutomation(true);
                      await submitRequestForAutomation(org.id, request.id);
                      setIsAttemptingAutomation(false);
                      refreshRequest();
                    }}
                  >
                    <img src="/assets/images/automation-icon.svg" alt="" /> Retry Now
                  </LoadingButton>
                </div>
              </div>
            ) : isFacebookAds(vendor) ? (
              <>
                {reconnectNudge} {metaAdsTOSReminder}
              </>
            ) : (
              <>{reconnectNudge}</>
            )
          }
        ></Callout>
      )}
      <FormControl component="fieldset" className={"mb-md w-100"}>
        <RadioGroup
          className={"flex--justify-start gap-xl"}
          style={{ flexWrap: "nowrap" }}
          aria-label="Vendor handling response"
          name={`vendor-${vendor.id}-data-handling`}
          onChange={(e) => onSelectionChange(vendor, e.target.value as VendorOutcomeOption)}
          value={value}
          row
        >
          <RadioControlLabel
            value="NO_DATA"
            disabled={disabled}
            disabledTooltip={"No data to delete"}
            label={<span className="text-t2 pr-xs radio-label-text">No data to delete</span>}
            recommended={instruction?.processingMethod == "INACCESSIBLE_OR_NOT_STORED"}
            narrow
            loading={updatePending && value == "NO_DATA"}
          />

          <RadioControlLabel
            value={deleteValue}
            disabled={disabled}
            disabledTooltip={deleteLabel}
            label={<span className="text-t2 pr-xs radio-label-text">{deleteLabel}</span>}
            recommended={instruction?.processingMethod == "DELETE"}
            narrow
            loading={updatePending && ["DATA_RETAINED", "DATA_DELETED"].includes(value)}
          />

          {!resolvedAutomatically && (
            <InstructionsButton
              vendorId={vendor.vendorId}
              hasInstructions={Boolean(
                vendor.deletionInstructions || instruction?.processingInstructions,
              )}
              isExpanded={instructionsState?.isExpanded(vendor.vendorId) ?? false}
              onToggle={instructionsState?.toggleInstructions ?? (() => {})}
            />
          )}
        </RadioGroup>
      </FormControl>
    </>
  );

  const nestedVendorList = customAudienceManagementVendor?.id == vendor.id && (
    <NestedVendorList
      managerName={customAudienceManagementVendor.name}
      nestedVendors={managedCustomAudienceVendors}
    />
  );
  const footers = (
    <>
      {isShopify(vendor) && <ShopifyFooter org={org} vendor={vendor} />}

      {isGoogleAnalytics(vendor) && !resolvedAutomatically && (
        <GoogleAnalyticsFooter
          org={org}
          request={request}
          authorizations={authorizations}
          outcome={vendorOutcomes[vendor.vendorId]}
          onSelectionChange={(selection) =>
            onSelectionChange(vendor, selection as VendorOutcomeOption)
          }
        />
      )}
    </>
  );

  // When the outcome is a lone callout, the vendor name is centered on it however many lines either wraps to. The
  // nested list and footers wrap onto a second line so they never stretch the line the two share (POL-7992).
  const outcomeIsCallout = resolvedAutomatically || awaitingConnectedSystem;

  return (
    <>
      <div
        key={`vendor-${vendor.id}`}
        className={clsx("flex-container pt-std pb-std border-top border-top--grey", {
          "flex--wrap flex--align-center": outcomeIsCallout,
        })}
      >
        <div>
          <div className="text-t4 w-256 flex-container flex--align-center pl-xs">
            <DataRecipientLogo dataRecipient={vendor} className="align-top mr-mdlg" />
            <span>{vendor.name}</span>
            {resolvedAutomatically && (
              <img
                src="/assets/images/automation-icon.svg"
                alt="Resolved automatically"
                className="ml-xs"
              />
            )}
          </div>
          {!outcomeIsCallout && nestedVendorList}
        </div>
        <div className={outcomeIsCallout ? "flex--expand-self" : "flex-grow"}>
          {rowContent}
          {!outcomeIsCallout && footers}
        </div>
        {outcomeIsCallout && (
          <div className="w-100 flex-container">
            <div className="w-256">{nestedVendorList}</div>
            <div className="flex--expand-self">{footers}</div>
          </div>
        )}
      </div>

      {/* Instructions content appears below the vendor row */}
      {!resolvedAutomatically && (
        <InstructionsContent
          instructions={vendor.deletionInstructions}
          processingInstructions={instruction?.processingInstructions}
          title="Deletion Instructions"
          isExpanded={instructionsState?.isExpanded(vendor.vendorId) ?? false}
        />
      )}
    </>
  );
};

type ShopifyFooterProps = {
  org: OrganizationDto;
  vendor: OrganizationDataRecipientDto;
  // authorizations: OrganizationAuthorizationsDto;
  // request: DataSubjectRequestDetailDto;
  // outcome: string | null;
  // onSelectionChange(selection: string);
};
const ShopifyFooter: React.FC<ShopifyFooterProps> = ({ org, vendor }) => {
  const [platformApps, platformAppsRequest] = usePlatformApps(org.id, vendor.vendorId);
  const installedApps = installedAppsRespectingDeletionForVendor(vendor, platformApps);

  const [showShopifyInstalledApps, setShowShopifyInstalledApps] = useState(false);

  const toggleShopifyInstalledApps = useCallback(
    (evt?: Event) => {
      evt?.preventDefault();
      setShowShopifyInstalledApps((x) => !x);
    },
    [setShowShopifyInstalledApps],
  );

  return (
    <NetworkRequestGate requests={[platformAppsRequest]}>
      {installedApps?.length > 0 ? (
        <Callout variant={CalloutVariant.Yellow} className={"mb-md"}>
          <VendorEmailDisplayDialog
            open={showShopifyInstalledApps}
            // @ts-ignore
            onClose={toggleShopifyInstalledApps}
            vendors={installedApps}
            title="Shopify Installed Apps"
          />
          Shopify will automatically delete data from your{" "}
          <a href="#" onClick={(e) => toggleShopifyInstalledApps(e as any)}>
            installed apps
          </a>
          .
        </Callout>
      ) : (
        <></>
      )}
    </NetworkRequestGate>
  );
};

type GoogleAnaltyicsFooterProps = {
  org: OrganizationDto;
  authorizations: OrganizationAuthorizationsDto;
  request: DataSubjectRequestDetailDto;
  outcome: string | null;
  onSelectionChange(selection: string);
};

const GoogleAnalyticsFooter: React.FC<GoogleAnaltyicsFooterProps> = ({
  org,
  authorizations,
  request,
  outcome,
  onSelectionChange,
}) => {
  const canAutodelete =
    request.hasGaUserId && Boolean(authorizations.hasGoogle) && Boolean(org.gaWebProperty);
  const hasAutodeleted = outcome == "DATA_DELETED";
  const hasMarkedNoData = outcome == "NO_DATA";
  const [autodeleteError, setAutodeleteError] = useState<string | null>(null);

  const api = useCallback(
    () => Api.dataRequest.autodeleteGoogleAnalytics(org.id, request.id),
    [org.id, request.id],
  );

  const { fetch, request: autodeleteRequest } = useActionRequest({
    api,
    notifySuccess: true,
    notifyError: false, // We'll handle errors ourselves
    onSuccess: () => {
      setAutodeleteError(null);
      onSelectionChange("DATA_DELETED");
    },
    onError: (networkRequestState, error) => {
      const response: any = (error as any)?.response;

      if (response?.status === 403) {
        setAutodeleteError("Your Google Analytics permissions have expired or have changed.");
      } else if (response?.status === 401) {
        setAutodeleteError("Your Google Analytics authentication has expired.");
      } else {
        setAutodeleteError(
          response?.data?.error ||
            networkRequestState.errorMessage ||
            "There was an error while processing your request.",
        );
      }
    },
    messages: {
      success: "Google Analytics data autodeleted successfully",
    },
  });

  useEffect(() => {
    if (!request.hasGaUserId && !hasMarkedNoData) {
      // If we can't auto-delete because the request doesn't have a GA ID associated with it, we mark as no data.
      onSelectionChange("NO_DATA");
    }
  }, [canAutodelete, hasMarkedNoData, onSelectionChange, request.hasGaUserId]);

  if (!canAutodelete) {
    return (
      <GoogleAnalyticsAutodeleteWarning
        hasGaUserId={request.hasGaUserId}
        hasGoogleAuthorization={authorizations.hasGoogle}
        hasGaWebProperty={Boolean(org.gaWebProperty)}
      />
    );
  }

  return (
    <div className="mt-sm mb-sm">
      {autodeleteError ? (
        <Callout variant={CalloutVariant.Yellow}>
          {autodeleteError}
          {autodeleteError && (
            <>
              <div className="mt-sm">
                Please visit the{" "}
                <Link to={Routes.privacyCenter.integrations}>Integrations page</Link> to reconnect
                to Google Analytics, then return to this request to try again.
              </div>
              <small>
                {autodeleteError.includes("permissions") ||
                autodeleteError.includes("authentication") ? (
                  <em>Google occasionally expires connections that are not used frequently.</em>
                ) : (
                  <em>
                    Often, these errors with Google Analytics can be resolved by reconnecting the
                    service.
                  </em>
                )}
              </small>
            </>
          )}
        </Callout>
      ) : (
        <LoadingButton
          variant="contained"
          color="primary"
          loading={autodeleteRequest.running}
          disabled={hasAutodeleted || autodeleteRequest.running}
          onClick={fetch}
        >
          Delete Consumer Data
        </LoadingButton>
      )}
    </div>
  );
};

type GoogleAnalyticsAutodeleteWarningProps = {
  hasGaUserId: boolean;
  hasGoogleAuthorization: boolean;
  hasGaWebProperty: boolean;
};
const GoogleAnalyticsAutodeleteWarning: React.FC<GoogleAnalyticsAutodeleteWarningProps> = ({
  hasGoogleAuthorization,
  hasGaWebProperty,
}) => {
  if (!hasGaWebProperty) {
    return (
      <Callout variant={CalloutVariant.Yellow} className="mb-sm">
        We cannot autodelete information from <strong>Google Analytics</strong> for this request
        because there is no Web Property ID set in the{" "}
        <Link to={Routes.privacyCenter.integrations}>Integrations page</Link>.
      </Callout>
    );
  }

  if (!hasGoogleAuthorization) {
    return (
      <Callout variant={CalloutVariant.Yellow} className="mb-sm">
        We cannot autodelete information from <strong>Google Analytics</strong> for this request
        because Google Analytics has not been authorized in the{" "}
        <Link to={Routes.privacyCenter.integrations}>Integrations page</Link>.
      </Callout>
    );
  }

  return (
    <Callout variant={CalloutVariant.Yellow} className="mb-sm">
      We cannot autodelete information from <strong>Google Analytics</strong> for this request
      because the consumer's Google Analytics User ID was not available at the time this request was
      submitted. We have marked as <strong>no data to delete</strong> automatically.
    </Callout>
  );
};
